import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Copy, Check, Loader2, KeyRound, Trash2, RefreshCw } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { api, InviteCodeResponse } from '@/lib/api';

type StatusFilter = 'all' | 'active' | 'used' | 'expired' | 'revoked';

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  active: 'default',
  used: 'secondary',
  expired: 'outline',
  revoked: 'destructive',
};

const ENV_LABELS: Record<string, string> = {
  local: 'Local',
  staging: 'Staging',
  prod: 'Production',
};

export function BetaKeyManager() {
  const [count, setCount] = useState(1);
  const [description, setDescription] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatedCodes, setGeneratedCodes] = useState<string[]>([]);
  const [copiedAll, setCopiedAll] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [error, setError] = useState('');

  const [availableEnvs, setAvailableEnvs] = useState<string[]>([]);
  const [selectedEnv, setSelectedEnv] = useState<string>('');

  const [existingCodes, setExistingCodes] = useState<InviteCodeResponse[]>([]);
  const [loadingCodes, setLoadingCodes] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [revoking, setRevoking] = useState<string | null>(null);

  const fetchEnvironments = useCallback(async () => {
    try {
      const { environments } = await api.adminGetInviteCodeEnvironments();
      setAvailableEnvs(environments);
      // Default to staging if available, otherwise first env
      if (environments.includes('staging')) {
        setSelectedEnv('staging');
      } else if (environments.length > 0) {
        setSelectedEnv(environments[0]);
      }
    } catch (err) {
      console.error('Failed to fetch invite code environments', err);
    }
  }, []);

  useEffect(() => {
    fetchEnvironments();
  }, [fetchEnvironments]);

  const fetchCodes = useCallback(async () => {
    if (!selectedEnv) return;
    try {
      setLoadingCodes(true);
      const result = await api.adminListInviteCodes({
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: 200,
        environment: selectedEnv,
      });
      setExistingCodes(result.inviteCodes);
    } catch (err) {
      console.error('Failed to fetch invite codes', err);
    } finally {
      setLoadingCodes(false);
    }
  }, [statusFilter, selectedEnv]);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const handleGenerate = async () => {
    if (count < 1 || count > 50) {
      setError('Count must be between 1 and 50');
      return;
    }
    if (!selectedEnv) {
      setError('Select an environment first');
      return;
    }

    setError('');
    setGenerating(true);
    setGeneratedCodes([]);
    setCopiedAll(false);

    try {
      const result = await api.adminCreateInviteCodes({
        count,
        grantRoles: ['user', 'beta_tester'],
        metadata: description ? { description } : undefined,
        environment: selectedEnv,
      });

      if (result.inviteCodes) {
        setGeneratedCodes(result.inviteCodes.map((c) => c.code));
      } else if (result.inviteCode) {
        setGeneratedCodes([result.inviteCode.code]);
      }

      fetchCodes();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate keys';
      setError(message);
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = async (text: string, index?: number) => {
    try {
      await navigator.clipboard.writeText(text);
      if (index !== undefined) {
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
      }
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      if (index !== undefined) {
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
      }
    }
  };

  const copyAllCodes = async () => {
    const text = generatedCodes.join('\n');
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2000);
  };

  const handleRevoke = async (code: string) => {
    setRevoking(code);
    try {
      await api.adminRevokeInviteCode(code, selectedEnv);
      fetchCodes();
    } catch (err) {
      console.error('Failed to revoke code', err);
    } finally {
      setRevoking(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-8">
      <Link
        to="/dev-console"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Dev Console
      </Link>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Beta Key Manager</h1>
        <p className="mt-2 text-muted-foreground">
          Generate and manage invite keys for closed beta participants
        </p>
      </div>

      {/* Environment Selector */}
      {availableEnvs.length > 0 && (
        <div className="mb-6">
          <Label className="text-sm text-muted-foreground mb-2 block">Environment</Label>
          <div className="flex gap-2">
            {availableEnvs.map((env) => (
              <Button
                key={env}
                variant={selectedEnv === env ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedEnv(env)}
              >
                {ENV_LABELS[env] || env}
              </Button>
            ))}
          </div>
        </div>
      )}

      {availableEnvs.length === 0 && (
        <Card className="mb-6">
          <CardContent className="py-4">
            <p className="text-sm text-amber-600 dark:text-amber-400">
              No invite code environments configured. Ensure the API service has the
              INVITE_CODES_TABLE_NAME environment variable set.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Generate Section */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Generate Beta Keys</CardTitle>
          <CardDescription>
            Create new invite keys that grant beta_tester access on signup
            {selectedEnv && (
              <span className="ml-1 font-medium">({ENV_LABELS[selectedEnv] || selectedEnv})</span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4">
            <div className="flex gap-4 items-end">
              <div className="flex-1 max-w-[200px]">
                <Label htmlFor="key-count">Number of keys (1-50)</Label>
                <Input
                  id="key-count"
                  type="number"
                  min={1}
                  max={50}
                  value={count}
                  onChange={(e) =>
                    setCount(Math.min(50, Math.max(1, parseInt(e.target.value) || 1)))
                  }
                />
              </div>
              <div className="flex-1">
                <Label htmlFor="key-description">Description (optional)</Label>
                <Input
                  id="key-description"
                  type="text"
                  placeholder="e.g., Batch for early testers"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <Button onClick={handleGenerate} disabled={generating || !selectedEnv}>
                {generating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <KeyRound className="h-4 w-4 mr-2" />
                    Generate
                  </>
                )}
              </Button>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            {generatedCodes.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-foreground">
                    Generated {generatedCodes.length} key{generatedCodes.length !== 1 ? 's' : ''}
                  </h3>
                  <Button variant="outline" size="sm" onClick={copyAllCodes}>
                    {copiedAll ? (
                      <>
                        <Check className="h-3 w-3 mr-1" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-3 w-3 mr-1" />
                        Copy All
                      </>
                    )}
                  </Button>
                </div>
                <div className="bg-muted rounded-lg p-3 space-y-1 max-h-80 overflow-y-auto">
                  {generatedCodes.map((code, i) => (
                    <div
                      key={code}
                      className="flex items-center justify-between py-1 px-2 rounded hover:bg-background/50 group"
                    >
                      <code className="text-sm font-mono text-foreground">{code}</code>
                      <button
                        onClick={() => copyToClipboard(code, i)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
                        title="Copy code"
                      >
                        {copiedIndex === i ? (
                          <Check className="h-3.5 w-3.5 text-green-500" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Existing Codes Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Existing Keys</CardTitle>
              <CardDescription>
                View and manage invite keys
                {selectedEnv && (
                  <span className="ml-1 font-medium">
                    ({ENV_LABELS[selectedEnv] || selectedEnv})
                  </span>
                )}
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchCodes}
              disabled={loadingCodes || !selectedEnv}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${loadingCodes ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Status Filter */}
          <div className="flex gap-2 mb-4">
            {(['all', 'active', 'used', 'expired', 'revoked'] as StatusFilter[]).map((status) => (
              <Button
                key={status}
                variant={statusFilter === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter(status)}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </Button>
            ))}
          </div>

          {!selectedEnv ? (
            <p className="text-center py-8 text-muted-foreground">Select an environment above</p>
          ) : loadingCodes ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading...
            </div>
          ) : existingCodes.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No invite keys found</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {existingCodes.map((invite) => (
                <div
                  key={invite.code}
                  className="flex items-center justify-between py-2 px-3 rounded-lg border bg-card"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <code className="text-sm font-mono text-foreground shrink-0">
                      {invite.code}
                    </code>
                    <Badge variant={STATUS_VARIANT[invite.status] || 'outline'}>
                      {invite.status}
                    </Badge>
                    {invite.metadata?.description && (
                      <span className="text-xs text-muted-foreground truncate">
                        {invite.metadata.description}
                      </span>
                    )}
                    {invite.usedByEmail && (
                      <span className="text-xs text-muted-foreground truncate">
                        Used by {invite.usedByEmail}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    <span className="text-xs text-muted-foreground">
                      {new Date(invite.createdAt).toLocaleDateString()}
                    </span>
                    {invite.status === 'active' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRevoke(invite.code)}
                        disabled={revoking === invite.code}
                        className="text-destructive hover:text-destructive"
                      >
                        {revoking === invite.code ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
